import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import Link from 'gatsby-link'
import pure from 'recompose/pure'
import withStyles from '@material-ui/core/styles/withStyles'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { ArticleMeta } from 'components/Meta'
import Divider from 'components/Divider'
import Tags from 'components/Tags'
import ShareBar from 'components/ShareBar'
import Featured from 'components/Featured'
import { pageStyle } from 'config/theme'
import formatDate from 'utils/formatDate'

const styles = (theme) => ({
  content: {
    ...pageStyle(theme),
    margin: 0,
    padding: 10,
    width: 700,
    [theme.breakpoints.down('sm')]: {
      width: '90%',
    },
  },
  thumbnail: {
    width: '100%',
    height: 430,
    objectFit: 'cover',
    [theme.breakpoints.down('sm')]: {
      height: '100%',
    },
  },
  title: {
    textTransform: 'none',
    fontWeight: 600,
    color: theme.palette.primary.main,
    padding: 10,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.4em',
    },
  },
  featured: {
    display: 'block',
    textAlign: 'center',
    color: theme.palette.featured.onWhiteStrong,
    fontWeight: 500,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.1em',
    },
  },
  authorWrapper: {
    color: theme.palette.text.soft,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1em',
    },
  },
  author: {
    fontWeight: 500,
    color: theme.palette.featured.onWhite,
    '&:hover': {
      opacity: 1,
    },
  },
  storyFrom: {
    fontWeight: 500,
    color: theme.palette.featured.onWhite,
    '&:hover': {
      opacity: 1,
    },
  },
  dateReadTime: {
    textAlign: 'center',
    paddingBottom: 10,
    color: theme.palette.text.soft,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1em',
    },
  },
  permissionMessage: {
    textAlign: 'center',
    fontSize: '0.8em',
    color: theme.palette.accent.notice,
    [theme.breakpoints.down('xs')]: {
      marginTop: 10,
    },
    '&:hover': {
      opacity: 1,
    },
  },
  paginateButton: {
    textTransform: 'none',
    background: theme.palette.primary.dark,
    transition: 'color 0.5s, background 0.5s',
    color: '#fff',
    margin: `0 ${theme.spacing.unit}px`,
    padding: 'auto 25px',
    borderRadius: theme.spacing.unit * 5,
    boxShadow: 'none',
    '&:hover': {
      color: theme.palette.primary.dark,
      background: theme.palette.borders.ghost,
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      fontSize: '0.8em',
    },
  },
  tagsContainer: {
    minHeight: 35,
    textAlign: 'center',
  },
})

const StoryTemplate = ({ classes, theme, data, pathContext }) => {
  const { previousPage, nextPage, storyBody } = pathContext
  const { markdownRemark: post, file } = data
  const baseUrl = 'https://healthfulinspired.com'

  const datePublished = new Date(post.frontmatter.date).toISOString()
  const dateModified = file.modifiedTime

  return (
    <Grid
      id="storyPage"
      spacing={24}
      justify="center"
      alignItems="center"
      style={{ padding: '20px 0' }}
      container
    >
      <ArticleMeta
        title={post.frontmatter.title}
        shortDescription={post.frontmatter.shortDescription}
        thumbnail={post.frontmatter.thumbnail}
        genre="health"
        tags={post.frontmatter.tags}
        wordCount={post.wordCount.words}
        datePublished={datePublished}
        dateModified={dateModified}
        articleBody={storyBody}
        excerpt={post.excerpt}
        path={post.fields.slug}
      />
      <Grid className={classes.content} item>
        <Typography variant="display1" align="center" className={classes.title}>
          {post.frontmatter.title}
        </Typography>
        {post.frontmatter.featured && (
          <Featured
            variant="subheading"
            className={classes.featured}
            doubleStar
          >
            This story was featured
          </Featured>
        )}
        {post.frontmatter.displayAuthor &&
          post.frontmatter.author && (
            <Typography align="center" className={classes.authorWrapper}>
              Author:{' '}
              <span className={classes.author}>{post.frontmatter.author}</span>
              {post.frontmatter.storyFrom &&
                post.frontmatter.storyFrom.toLowerCase() !==
                  post.frontmatter.author.toLowerCase() && (
                  <Fragment>
                    {' '}
                    as told by{' '}
                    <span className={classes.storyFrom}>
                      {post.frontmatter.storyFrom}
                    </span>
                  </Fragment>
                )}
            </Typography>
          )}
        <Typography className={classes.dateReadTime}>
          {formatDate(post.frontmatter.date)} {'\u2022'} {post.timeToRead} min
          read
        </Typography>
        <img
          className={classes.thumbnail}
          src={post.frontmatter.thumbnail}
          alt={post.frontmatter.title}
          title={post.frontmatter.title}
        />
        <ShareBar
          baseUrl={baseUrl}
          url={`${baseUrl}${post.fields.slug}`}
          thumbnail={post.frontmatter.thumbnail}
          title={post.frontmatter.title}
          excerpt={post.excerpt}
          tags={post.frontmatter.tags}
        />
        {post.frontmatter.permission && (
          <Typography variant="caption" className={classes.permissionMessage}>
            This story was given permission by the author to be featured and
            presented.
            <br /> Follow the link at the end to see the original page.
          </Typography>
        )}
        <div
          className="markdown"
          dangerouslySetInnerHTML={{ __html: post.html }}
        />
        {post.frontmatter.displaySource &&
          post.frontmatter.source && (
            <Typography
              align="center"
              variant="body1"
              style={{ padding: '15px 0', textTransform: 'none' }}
            >
              This article was originally posted{' '}
              <a href={post.frontmatter.source} target="_blank">
                here
              </a>
            </Typography>
          )}
        <EnhancedArticleNavigator
          previousPage={previousPage}
          nextPage={nextPage}
          className={classes.paginateButton}
        />
        <Divider offset={25} />
        <div className={classes.tagsContainer}>
          <Typography variant="body2">tags: &nbsp;</Typography>
          <Tags tags={post.frontmatter.tags} />
        </div>
      </Grid>
      <script async defer src="http://www.instagram.com/embed.js" />
      <style>{`
        .markdown img {
          width: 100%;
          max-height: 100vh;
          object-fit: cover;
          border: 1px solid ${theme.palette.borders.cloudier};
        }
        .markdown {
          color: ${theme.palette.text.main};
        }
        table {
          border: 1px solid ${theme.palette.primary.main};
        }
        table th {
          background: ${theme.palette.primary.main};
          color: #fff;
          padding: 10px;
        }
        table td {
          border: 1px solid ${theme.palette.borders.cloud};
        }
        h1, h2, h3, h4, h5, h6 {
          line-height: 1em;
        }
        h1 {
          padding: 20px 0;
        }
        .instagram-media {
          margin: auto !important;
        }
        @media (max-width: 600px) {
          .markdown h1, h2, h3 {
            font-size: 1.4em;
          }
          .markdown img {
            height: 100%;
          }
        }
      `}</style>
    </Grid>
  )
}

StoryTemplate.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  pathContext: PropTypes.shape({
    previousPage: PropTypes.string,
    nextPage: PropTypes.string,
    storyBody: PropTypes.string.isRequired,
  }).isRequired,
}

function ArticleNavigator({ previousPage, nextPage, className }) {
  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Button
        component={previousPage && Link}
        to={previousPage}
        variant="flat"
        size="medium"
        className={className}
        style={{ opacity: !previousPage && 0.35 }}
        disabled={!previousPage}
      >
        Previous article
      </Button>
      <Button
        component={nextPage && Link}
        to={nextPage}
        variant="flat"
        size="medium"
        className={className}
        style={{ opacity: !nextPage && 0.35 }}
        disabled={!nextPage}
      >
        Next article
      </Button>
    </div>
  )
}

const EnhancedArticleNavigator = pure(ArticleNavigator)

// eslint-disable-next-line
export const query = graphql`
  query StoryQuery(
    $path: String!
    $fileAbsolutePath: String!
    $thumbnailName: String!
  ) {
    markdownRemark(fields: { slug: { eq: $path } }) {
      html
      excerpt(pruneLength: 300)
      timeToRead
      wordCount {
        words
      }
      fields {
        slug
      }
      frontmatter {
        title
        date
        author
        displayAuthor
        storyFrom
        permission
        sponsored
        featured
        thumbnail
        source
        displaySource
        shortDescription
        tags
        category
      }
    }
    file(absolutePath: { eq: $fileAbsolutePath }) {
      modifiedTime
    }
    imageSharp(sizes: { originalName: { eq: $thumbnailName } }) {
      sizes(maxWidth: 700, traceSVG: { color: "#285978" }) {
        ...GatsbyImageSharpSizes_tracedSVG
      }
    }
  }
`

export default withStyles(styles, { withTheme: true })(StoryTemplate)



// WEBPACK FOOTER //
// ./src/templates/story.js